import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, Clock, Tag, MessageCircle, ArrowUpRight } from "lucide-react";
import { POSTS, getPost } from "../blog/posts";
import { Seo } from "../components/Seo";
import { BRAND, WHATSAPP_LINK } from "../brand";

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  const url = `${BRAND.siteUrl}/blog/${post.slug}`;
  const related = POSTS.filter((p) => p.slug !== post.slug).slice(0, 3);

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.description,
    datePublished: post.date,
    dateModified: post.date,
    image: `${BRAND.siteUrl}${post.image}`,
    keywords: post.keywords,
    author: { "@type": "Organization", name: "ooloo" },
    publisher: {
      "@type": "Organization",
      name: "ooloo",
      logo: { "@type": "ImageObject", url: `${BRAND.siteUrl}/brand/hero-portrait.png` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  return (
    <main data-testid={`blog-post-${post.slug}`} className="bg-[#f7f5f2]">
      <Seo
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={post.image}
        type="article"
        jsonLd={jsonLd}
      />

      <article className="max-w-3xl mx-auto px-6 md:px-10 pt-12 pb-16">
        <Link
          to="/blog"
          data-testid="blog-post-back"
          className="inline-flex items-center gap-2 text-sm font-bold text-[#5c5a56] hover:text-[#0a0a0a] mb-10"
        >
          <ArrowLeft size={16} /> All articles
        </Link>

        <div className="flex items-center gap-3 text-xs font-bold tracking-widest uppercase text-[#5c5a56]">
          <span className="inline-flex items-center gap-1.5">
            <Tag size={12} /> {post.category}
          </span>
          <span className="w-1 h-1 rounded-full bg-[#5c5a56]" />
          <span className="inline-flex items-center gap-1.5">
            <Clock size={12} /> {post.readMinutes} min read
          </span>
          <span className="w-1 h-1 rounded-full bg-[#5c5a56]" />
          <time dateTime={post.date}>
            {new Date(post.date).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "long",
              year: "numeric",
            })}
          </time>
        </div>

        <h1 className="font-display mt-5 text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.02] text-[#0a0a0a]">
          {post.title}
        </h1>

        <p className="mt-6 text-xl text-[#5c5a56] leading-relaxed font-medium">
          {post.excerpt}
        </p>

        <div className="mt-10 bento-card overflow-hidden p-0">
          <img src={post.image} alt={post.title} className="w-full h-auto" />
        </div>

        <div
          className="ooloo-prose mt-12 text-[#0a0a0a]"
          dangerouslySetInnerHTML={{ __html: post.body }}
        />

        <div className="mt-16 dark-card grain p-8 md:p-10">
          <div className="relative">
            <p className="eyebrow text-white/60">Try ooloo</p>
            <h2 className="font-display mt-3 text-3xl md:text-4xl font-black tracking-tighter leading-[1] text-white">
              Book kitchen help in 30 minutes.
            </h2>
            <p className="mt-3 text-white/75 max-w-xl">
              On-demand kitchen support at your doorstep across Lucknow — Gomtinagar,
              Gomtinagar Extension and Sushant Golf City.
            </p>
            <a
              href={WHATSAPP_LINK}
              target="_blank"
              rel="noreferrer"
              data-testid="blog-post-whatsapp"
              className="btn-whatsapp mt-6"
            >
              <MessageCircle size={18} strokeWidth={2.5} /> Book on WhatsApp
            </a>
          </div>
        </div>
      </article>

      <section className="bg-[#efebe6] py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10">
          <p className="eyebrow">Keep reading</p>
          <h2 className="font-display mt-3 text-3xl md:text-5xl font-black tracking-tighter leading-[0.95] text-[#0a0a0a]">
            More from ooloo.
          </h2>
          <div className="mt-10 grid md:grid-cols-3 gap-5">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                data-testid={`blog-related-${p.slug}`}
                className="bento-card overflow-hidden p-0 group"
              >
                <div className="relative h-[200px] overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6">
                  <p className="text-xs font-bold tracking-widest uppercase text-[#5c5a56]">
                    {p.category}
                  </p>
                  <h3 className="mt-3 font-display text-lg font-extrabold tracking-tight leading-tight text-[#0a0a0a]">
                    {p.title}
                  </h3>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-[#0a0a0a] link-underline">
                    Read <ArrowUpRight size={14} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default BlogPost;
